"use client";

import { useEffect, useRef } from "react";
import { testimonials } from "@/data/testimonials";

function ReviewSchemaScript() {
  const data = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Dev Cubix",
    url: "https://devcubix.com",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: String(testimonials.length),
      bestRating: "5",
      worstRating: "1",
    },
    review: testimonials.map((t) => ({
      "@type": "Review",
      author: { "@type": "Person", name: t.name },
      reviewBody: t.quote,
      reviewRating: {
        "@type": "Rating",
        ratingValue: "5",
        bestRating: "5",
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

export default function Testimonials() {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let animationId: number;
    let scrollPos = 0;
    let paused = false;

    function scroll() {
      if (!paused) {
        scrollPos += 0.5;
        if (el && scrollPos >= el.scrollWidth / 2) scrollPos = 0;
        el?.scrollTo({ left: scrollPos });
      }
      animationId = requestAnimationFrame(scroll);
    }
    animationId = requestAnimationFrame(scroll);

    const pause = () => { paused = true; };
    const resume = () => { paused = false; };
    el.addEventListener("mouseenter", pause);
    el.addEventListener("mouseleave", resume);
    el.addEventListener("touchstart", pause, { passive: true });
    el.addEventListener("touchend", resume, { passive: true });

    return () => {
      cancelAnimationFrame(animationId);
      el.removeEventListener("mouseenter", pause);
      el.removeEventListener("mouseleave", resume);
      el.removeEventListener("touchstart", pause);
      el.removeEventListener("touchend", resume);
    };
  }, []);

  const doubled = [...testimonials, ...testimonials];

  return (
    <section className="pt-10 sm:pt-16 pb-10 sm:pb-12 bg-surface">
      <ReviewSchemaScript />
      {/* Header — matches DM page_08: "Trusted by People / Chosen By Brands" */}
      <div className="max-w-[960px] mx-auto px-6 mb-8 sm:mb-14 text-center">
        <p className="section-badge mb-4 mx-auto">Referral From People</p>
        <h2 className="text-2xl sm:text-3xl md:text-[40px] font-bold text-center leading-[1.2] font-ui">
          <span>Trusted by People</span>
          <br />
          <span className="font-serif italic text-purple-light">Chosen By Brands</span>
        </h2>
      </div>

      {/* Scrolling testimonial cards */}
      <div ref={scrollRef} className="flex gap-4 sm:gap-6 overflow-x-auto hide-scrollbar px-4 sm:px-6">
        {doubled.map((t, i) => (
          <div
            key={`${t.name}-${i}`}
            className="flex-shrink-0 w-[280px] sm:w-[320px] md:w-[340px] bg-surface-light border border-card-border rounded-2xl p-5 sm:p-6 md:p-7 flex flex-col justify-between hover:border-foreground/10 transition-colors"
          >
            {/* Quote */}
            <p className="text-sm text-foreground/80 mb-8 font-body leading-relaxed line-clamp-5 break-words">
              &ldquo;{t.quote}&rdquo;
            </p>
            {/* Author */}
            <div className="flex items-center gap-3 pt-4 border-t border-card-border">
              <img
                src={t.avatar}
                alt={t.name}
                className="w-11 h-11 rounded-full object-cover shadow-md"
              />
              <div>
                <p className="text-sm font-semibold text-foreground">{t.name}</p>
                <p className="text-xs text-foreground/60">{t.title}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
